// src/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  IndianRupee, Users, ShoppingBag, Package,
  ArrowRight, TrendingUp,
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, color, to }) {
  const card = (
    <div className={`bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all ${to ? 'cursor-pointer hover:-translate-y-0.5' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-gray-400 font-body text-sm">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <p className="font-display font-bold text-2xl text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 font-body mt-1">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

const STATUS_COLORS = {
  confirmed:  'bg-blue-100 text-blue-700',
  processing: 'bg-amber-100 text-amber-700',
  shipped:    'bg-purple-100 text-purple-700',
  delivered:  'bg-green-100 text-green-700',
  cancelled:  'bg-red-100 text-red-700',
};

export default function AdminDashboard() {
  const [stats,        setStats]        = useState({ revenue: 0, orders: 0, merchants: 0, products: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersSnap, merchantsSnap, productsSnap] = await Promise.all([
          getDocs(collection(db, 'orders')),
          getDocs(collection(db, 'merchants')),
          getDocs(collection(db, 'products')),
        ]);

        const allOrders = ordersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const revenue   = allOrders.reduce((s, o) => s + (o.total || 0), 0);

        allOrders.sort((a, b) =>
          (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0)
        );

        setStats({
          revenue,
          orders:    allOrders.length,
          merchants: merchantsSnap.size,
          products:  productsSnap.size,
        });
        setRecentOrders(allOrders.slice(0, 8));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Welcome banner — indigo/purple for admin */}
      <div className="relative bg-gradient-to-r from-indigo-600 to-violet-700 rounded-3xl p-6 text-white overflow-hidden">
        <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full" />
        <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/5 rounded-full" />
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-white/20 rounded-l-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-white text-xs font-bold">A</span>
            </div>
            <p className="font-body text-indigo-200 text-sm font-semibold tracking-wider uppercase">Admin Panel</p>
          </div>
          <h2 className="font-display font-bold text-2xl mb-1">Platform Overview</h2>
          <p className="text-indigo-200 font-body text-sm">
            Full control over merchants, orders and the CoupleCart platform.
          </p>
        </div>
      </div>

      {/* Stats — indigo/violet colors */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={IndianRupee}
          label="Total Revenue"
          value={`₹${stats.revenue.toLocaleString('en-IN')}`}
          sub="All time"
          color="bg-indigo-500"
        />
        <StatCard
          icon={ShoppingBag}
          label="Total Orders"
          value={stats.orders}
          sub="All time"
          color="bg-violet-500"
          to="/admin/orders"
        />
        <StatCard
          icon={Users}
          label="Merchants"
          value={stats.merchants}
          sub="Active sellers"
          color="bg-purple-500"
          to="/admin/merchants"
        />
        <StatCard
          icon={Package}
          label="Products"
          value={stats.products}
          sub="Listed"
          color="bg-fuchsia-500"
          to="/admin/products"
        />
      </div>

      {/* Quick actions */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link
          to="/admin/merchants"
          className="flex items-center justify-between bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 group-hover:bg-indigo-500 rounded-xl flex items-center justify-center transition-colors">
              <Users size={20} className="text-indigo-500 group-hover:text-white transition-colors" />
            </div>
            <div>
              <p className="font-body font-semibold text-gray-900 text-sm">Manage Merchants</p>
              <p className="text-gray-400 font-body text-xs">Approve, set commission, view profiles</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-gray-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
        </Link>

        <Link
          to="/admin/orders"
          className="flex items-center justify-between bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:border-violet-300 hover:shadow-md transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-50 group-hover:bg-violet-500 rounded-xl flex items-center justify-center transition-colors">
              <ShoppingBag size={20} className="text-violet-500 group-hover:text-white transition-colors" />
            </div>
            <div>
              <p className="font-body font-semibold text-gray-900 text-sm">All Orders</p>
              <p className="text-gray-400 font-body text-xs">View and manage platform orders</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-gray-300 group-hover:text-violet-500 group-hover:translate-x-1 transition-all" />
        </Link>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-indigo-500 rounded-full" />
            <h3 className="font-display font-semibold text-gray-900">Recent Orders</h3>
          </div>
          <Link
            to="/admin/orders"
            className="text-indigo-500 hover:text-indigo-600 font-body text-sm font-medium flex items-center gap-1 transition-colors"
          >
            View All <ArrowRight size={14} />
          </Link>
        </div>

        {recentOrders.length === 0 ? (
          <div className="p-10 text-center">
            <ShoppingBag size={36} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 font-body text-sm">No orders yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentOrders.map((order) => (
              <div key={order.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors">
                <div className="flex -space-x-2 shrink-0">
                  {order.items?.slice(0, 2).map((item, i) => (
                    <img key={i} src={item.image} alt={item.name}
                      className="w-10 h-10 rounded-xl object-cover border-2 border-white" />
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body font-bold text-gray-900 text-sm">
                    #{order.id.slice(-6).toUpperCase()}
                  </p>
                  <p className="text-gray-400 font-body text-xs truncate">
                    {order.shippingAddress?.name} · {order.items?.length} item(s)
                  </p>
                </div>
                <p className="font-display font-bold text-gray-900 text-sm shrink-0">
                  ₹{order.total?.toLocaleString('en-IN')}
                </p>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 capitalize ${STATUS_COLORS[order.status] || STATUS_COLORS.confirmed}`}>
                  {order.status || 'confirmed'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
